import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { logger } from '../../lib/logger';

/**
 * Fetches claim/revenue/comparison trends and exposes export-to-CSV/PDF
 * actions. Memoised to avoid re-fetching when the parent re-renders.
 */
export function useTrends({ agencyId = null } = {}) {
  const [claimsTrend, setClaimsTrend] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [days, setDays] = useState('30');
  const [granularity, setGranularity] = useState('daily');
  const [compareTo, setCompareTo] = useState('previous_month');

  const fetchTrends = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, granularity });
      if (agencyId) params.append('agency_id', agencyId);
      const cmpQs = `compare_to=${compareTo}${agencyId ? `&agency_id=${agencyId}` : ''}`;
      const [claimsRes, revenueRes, comparisonRes] = await Promise.all([
        api.get(`/trends/claims?${params}`),
        api.get(`/trends/revenue?${params}`),
        api.get(`/trends/comparison?${cmpQs}`),
      ]);
      setClaimsTrend(claimsRes.data.trend || []);
      setRevenueTrend(revenueRes.data.trend || []);
      setComparison(comparisonRes.data || null);
    } catch (error) {
      logger.error('Failed to fetch trends:', error);
    } finally {
      setLoading(false);
    }
  }, [days, granularity, agencyId, compareTo]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  const downloadBlob = (data, filename, mime) => {
    const url = window.URL.createObjectURL(new Blob([data], mime ? { type: mime } : undefined));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportTrend = useCallback(
    async (format) => {
      setExporting(true);
      try {
        const params = new URLSearchParams({ days });
        if (format === 'csv') params.append('data_type', 'all');
        if (agencyId) params.append('agency_id', agencyId);
        const res = await api.get(`/trends/export/${format}?${params}`, { responseType: 'blob' });
        const today = new Date().toISOString().split('T')[0];
        downloadBlob(
          res.data,
          `trend_report_${today}.${format}`,
          format === 'pdf' ? 'application/pdf' : undefined
        );
        toast.success(`${format.toUpperCase()} exported successfully`);
      } catch (error) {
        logger.error('Export failed:', error);
        toast.error(`Failed to export ${format.toUpperCase()}`);
      } finally {
        setExporting(false);
      }
    },
    [days, agencyId]
  );

  return {
    claimsTrend,
    revenueTrend,
    comparison,
    loading,
    exporting,
    days,
    setDays,
    granularity,
    setGranularity,
    compareTo,
    setCompareTo,
    refresh: fetchTrends,
    exportCsv: () => exportTrend('csv'),
    exportPdf: () => exportTrend('pdf'),
  };
}
